import React, { useState } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Robot, ArrowRight, Lock, CheckCircle } from "@phosphor-icons/react";
import Field from "@/components/Field";

export default function ResetPassword() {
  const [params] = useSearchParams();
  const nav = useNavigate();
  const token = params.get("token") || "";
  const [pw, setPw] = useState("");
  const [pw2, setPw2] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (pw.length < 6) { setError("Password must be at least 6 characters."); return; }
    if (pw !== pw2) { setError("Passwords do not match."); return; }
    setBusy(true); setError("");
    try {
      await api.post("/auth/reset-password", { token, password: pw });
      setDone(true);
      setTimeout(() => nav("/login"), 2500);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-8 bg-white">
      <div className="w-full max-w-md">
        <Link to="/" className="flex items-center gap-2 mb-8">
          <div className="w-9 h-9 bg-[var(--ink)] flex items-center justify-center">
            <Robot size={20} weight="duotone" color="#FFD600" />
          </div>
          <span className="font-display text-2xl">Robocope</span>
        </Link>

        <div className="overline text-[var(--ink-soft)]">/ Set a new password</div>
        <h1 className="font-display text-4xl tracking-tight mt-2 mb-2">Choose a new password</h1>
        <p className="text-[var(--ink-soft)] mb-8">Pick something at least 6 characters long.</p>

        {!token ? (
          <div className="cell p-4 bg-red-50 text-red-700 border-red-200 text-sm" data-testid="no-token-error">
            Missing reset token. <Link to="/forgot-password" className="underline">Request a new link</Link>.
          </div>
        ) : done ? (
          <div className="cell-strong bg-[var(--accent)] p-6" data-testid="reset-success">
            <CheckCircle size={28} weight="fill" />
            <div className="font-display text-2xl mt-2">Password updated.</div>
            <p className="text-sm mt-1">Redirecting you to log in…</p>
          </div>
        ) : (
          <form onSubmit={submit} className="space-y-4" data-testid="reset-form">
            <Field label="New password" icon={Lock}>
              <input type="password" required minLength={6} value={pw} onChange={(e) => setPw(e.target.value)} className="input-rc pl-10" data-testid="new-password-input" />
            </Field>
            <Field label="Confirm new password" icon={Lock}>
              <input type="password" required minLength={6} value={pw2} onChange={(e) => setPw2(e.target.value)} className="input-rc pl-10" data-testid="confirm-password-input" />
            </Field>
            {error && <div className="cell p-3 text-sm bg-red-50 text-red-700 border-red-200" data-testid="reset-error">{error}</div>}
            <button type="submit" disabled={busy} className="btn-primary w-full justify-center" data-testid="reset-submit-btn">
              {busy ? "Working…" : "Update password"} <ArrowRight size={18} weight="bold" />
            </button>
          </form>
        )}
      </div>
    </div>
  );
}
